import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeyValueDTO, PrincipalService } from '@ms/core';
import { EventManager } from '@ms/core/service/event-manager.service';
import { patchState, signalStore, withComputed, withHooks, withMethods } from '@ngrx/signals';
import {
    addEntity,
    removeAllEntities,
    removeEntity,
    setAllEntities,
    updateEntity,
    withEntities,
} from '@ngrx/signals/entities';
import { Subscription } from 'rxjs';
import { SchoolClassList, SchoolClassSearch } from '../school-class/model/school-class.model';
import { GradeLevelsService } from '../service';
import { GradeLevelStore } from './gradelevel.store';

export const SchoolClassStore = signalStore(
    { providedIn: 'root' },
    withDevtools('campus-school-class'),
    withEntities<SchoolClassList>(),
    // To use localstorage : https://www.npmjs.com/package/@angular-architects/ngrx-toolkit
    //withStorageSync('campus-custom-parameter'),
    withComputed(store => ({
        size: computed(() => store.entities().length),
        loaded: signal<boolean>(false),
        activeSchoolId: signal<string>(undefined),
        employeeId: signal<string>(undefined),
        event: signal<Subscription>(undefined),
    })),
    withMethods(store => {
        const gradeLevelStore = inject(GradeLevelStore);
        const em = inject(EventManager);
        return {
            set(items: SchoolClassList[]) {
                store.loaded.set(items.length > 0);
                patchState(store, setAllEntities([...items]));
            },

            create(item: SchoolClassList) {
                patchState(store, addEntity({ ...item }));
            },

            remove(id: string) {
                patchState(store, removeEntity(id));
            },

            get(id: string): SchoolClassList {
                return { ...store.entityMap()[id] };
            },

            update(item: SchoolClassList) {
                patchState(store, updateEntity({ id: item.id, changes: { ...item } }));
            },

            findAll(search: SchoolClassSearch): SchoolClassList[] {
                const result = [];
                store.ids().forEach(id => {
                    const c: SchoolClassList = store.entityMap()[id];
                    if (
                        (!search.academicYearId || c.academicPeriod == search.academicYearId) &&
                        (!search.gradeLevelId || c.gradeLevelId == search.gradeLevelId) &&
                        (gradeLevelStore.hasPermission(c.gradeLevelId) || c.teacherId == store.employeeId())
                    ) {
                        result.push({ ...c });
                    }
                });
                em.httpEnd();
                return result;
            },

            getAllActive(): SchoolClassList[] {
                const result = [];
                store.ids().forEach(id => {
                    const c: SchoolClassList = store.entityMap()[id];
                    if (
                        c.active &&
                        (gradeLevelStore.hasPermission(c.gradeLevelId) || c.teacherId == store.employeeId())
                    ) {
                        result.push({ ...c });
                    }
                });
                em.httpEnd();
                return result;
            },

            getAllActiveKeyValue(academicYearId: string, gradeLevelId: string): KeyValueDTO[] {
                const result = [];
                store.ids().forEach(id => {
                    const c: SchoolClassList = store.entityMap()[id];
                    if (
                        c.active &&
                        (!academicYearId || academicYearId == c.academicPeriod) &&
                        (!gradeLevelId || gradeLevelId == c.gradeLevelId) &&
                        (gradeLevelStore.hasPermission(c.gradeLevelId) || c.teacherId == store.employeeId())
                    ) {
                        result.push(new KeyValueDTO(c.id, c.name, c.gradeLevelId, c.teacherId, c.gradeLevelName));
                    }
                });
                em.httpEnd();
                return result;
            },

            search(academicYearId: string): KeyValueDTO[] {
                const result = [];
                store.ids().forEach(id => {
                    const c: SchoolClassList = store.entityMap()[id];
                    if (
                        c.active &&
                        c.academicPeriod == academicYearId &&
                        (gradeLevelStore.hasPermission(c.gradeLevelId) || c.teacherId == store.employeeId())
                    ) {
                        result.push(new KeyValueDTO(c.id, c.name, c.gradeLevelId, c.teacherId, c.gradeLevelName));
                    }
                });
                em.httpEnd();
                return result;
            },

            setActive(id: string, isActive: boolean) {
                patchState(store, updateEntity({ id, changes: { active: isActive } }));
            },
            clear(schoolId: string) {
                console.log('CLEAR CLASS STORE');
                store.loaded.set(false);
                store.activeSchoolId.set(schoolId);
                if (store.size() > 0) {
                    patchState(store, removeAllEntities());
                }
            },
        };
    }),
    withHooks({
        onInit: store => {
            const principal = inject(PrincipalService);
            const gradeLevelService = inject(GradeLevelsService);
            const gradeLevelStore = inject(GradeLevelStore);
            const em = inject(EventManager);
            store.event.set(
                em.subscribe('school_class_changed', () => {
                    store.clear(store.activeSchoolId());
                })
            );
            principal.state$.pipe(takeUntilDestroyed()).subscribe(state => {
                if (!state.authenticated) {
                    store.clear(undefined);
                } else {
                    if (state.user?.activeSchool?.id != store.activeSchoolId()) {
                        store.clear(state.user.activeSchool.id);
                    }
                    store.employeeId.set(state.user?.teacher?.id);
                    if (!gradeLevelStore.loaded()) {
                        gradeLevelService.getAll().subscribe(() => {});
                    }
                }
            });
        },
        onDestroy: store => {
            store.clear(undefined);
            if (store.event()) {
                store.event().unsubscribe();
            }
        },
    })
);
